import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Box, Paper, TextField, Button, Typography, Alert, CircularProgress } from '@mui/material';
import api from '../services/api';

const SITE_TOKEN_KEY = 'site_token';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [hasAdmin, setHasAdmin] = useState(true);

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await api.get('/auth/has-admin');
        setHasAdmin(res.data.hasAdmin);
      } catch (err) {
        // If the check fails, assume admin exists to avoid exposing bootstrap form wrongly
        setHasAdmin(true);
      } finally {
        setLoading(false);
      }
    };
    checkAdmin();
  }, []);

  const handleLoginChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
  };

  const handleRegisterChange = (e) => {
    setRegisterData({ ...registerData, [e.target.name]: e.target.value });
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/auth/login', loginData);
      if (response.data.user.role === 'admin') {
        localStorage.setItem(SITE_TOKEN_KEY, response.data.token);
        navigate('/admin/dashboard');
      } else {
        setError('Access denied. Admin only.');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Login failed');
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setRegisterError('');
    setRegisterSuccess('');

    if (!registerData.name || !registerData.email || !registerData.password || !registerData.confirmPassword) {
      setRegisterError('All fields are required.');
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      setRegisterError('Passwords do not match.');
      return;
    }

    try {
      const response = await api.post('/auth/bootstrap-admin', {
        name: registerData.name,
        email: registerData.email,
        password: registerData.password,
      });

      // Log in newly created admin
      localStorage.setItem(SITE_TOKEN_KEY, response.data.token);
      setRegisterSuccess('Admin account created successfully.');
      navigate('/admin/dashboard');
    } catch (err) {
      setRegisterError(err.response?.data?.message || 'Failed to create admin account.');
    }
  };

  return (
    <Container sx={{ py: 8 }}>
      <Box sx={{ maxWidth: 400, mx: 'auto' }}>
        <Paper sx={{ p: 4 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : hasAdmin ? (
            <>
              <Typography variant="h4" gutterBottom align="center">
                Admin Login
              </Typography>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <form onSubmit={handleLoginSubmit}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={loginData.email}
                  onChange={handleLoginChange}
                  required
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  required
                  margin="normal"
                />
                <Button type="submit" variant="contained" fullWidth size="large" sx={{ mt: 3 }}>
                  Login
                </Button>
              </form>
            </>
          ) : (
            <>
              <Typography variant="h4" gutterBottom align="center">
                Create Admin Account
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                No admin account exists yet. Create the first admin for this system.
              </Typography>
              {registerError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {registerError}
                </Alert>
              )}
              {registerSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {registerSuccess}
                </Alert>
              )}
              <form onSubmit={handleRegisterSubmit}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={registerData.name}
                  onChange={handleRegisterChange}
                  required
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={registerData.email}
                  onChange={handleRegisterChange}
                  required
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={registerData.password}
                  onChange={handleRegisterChange}
                  required
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={registerData.confirmPassword}
                  onChange={handleRegisterChange}
                  required
                  margin="normal"
                />
                <Button type="submit" variant="contained" fullWidth size="large" sx={{ mt: 3 }}>
                  Create Admin
                </Button>
              </form>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default AdminLogin;

